import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setDestroy } from "components/modalx/modalx.slice";
import Notice from "components/tw/notice";
import {
  btnPrimaryCls,
  btnSecondaryCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { upload, isEmptyUploadError } from "services/upload-file.service";
import { attachOrderDocument } from "services/orders.service";
import { externalContractErrorMessage } from "utils/external-contract-errors";
import { EXTERNAL_NUMBER_MAX_LENGTH } from "features/order-form/external-contract-fields";

type Props = {
  companyId: string;
  orderId: string;
  /** The contract number as the order has it — editable here. */
  externalNumber?: string | null;
  /** The contract date (YYYY-MM-DD) as the order has it. */
  contractDate?: string;
  /** Whether a document is already attached, so this replaces it. */
  hasDocument?: boolean;
  /** Called after the document was attached, so the caller can refetch. */
  onDone?: () => void;
};

const looksLikePdf = (file: File) =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name);

/**
 * Attach the signed PDF of an external contract — a modalX component.
 *
 * Uploads the file, then asks the server to make it the order's main document.
 * The server checks the file really is a PDF and refuses once the current
 * document has been sent for signing; this only saves a round trip for the
 * obvious case. Every failure closes the "working" state and says why, unlike
 * the older upload button that left a spinner up on error.
 */
export default function AttachExternalDocument({
  companyId,
  orderId,
  externalNumber: initialNumber,
  contractDate: initialDate,
  hasDocument,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [externalNumber, setExternalNumber] = useState(initialNumber || "");
  const [contractDate, setContractDate] = useState(initialDate || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const close = () => dispatch(setDestroy());

  const handleFile = (picked: File | null) => {
    setError("");
    if (picked && !looksLikePdf(picked)) {
      setFile(null);
      setError(t("external_document_not_pdf"));
      return;
    }
    setFile(picked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError(t("external_document_choose_file"));
      return;
    }
    if (!externalNumber.trim()) {
      setError(t("order_external_number_required"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data: fileId } = await upload(file, companyId);
      await attachOrderDocument({
        companyId,
        orderId,
        data: {
          fileId,
          externalNumber: externalNumber.trim(),
          ...(contractDate ? { date: contractDate } : {}),
        },
      });
      toast.success(
        hasDocument ? t("external_document_replaced") : t("external_document_attached")
      );
      onDone?.();
      close();
    } catch (err: any) {
      // An empty file was already toasted by upload() — no second message.
      if (!isEmptyUploadError(err)) setError(externalContractErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-sm text-zinc-700 dark:text-zinc-200">
      {hasDocument ? (
        <Notice tone="warning">
          <p>{t("replace_external_document_confirm")}</p>
        </Notice>
      ) : (
        <p>{t("attach_external_document_intro")}</p>
      )}

      {error ? (
        <Notice tone="critical">
          <p>{error}</p>
        </Notice>
      ) : null}

      <div>
        <label htmlFor="external-document-file" className={labelCls}>
          {t("external_document_file")}
        </label>
        <label
          htmlFor="external-document-file"
          className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-zinc-300 px-3 py-3 hover:bg-zinc-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
        >
          <FileUp className="size-4 shrink-0 text-zinc-500" aria-hidden="true" />
          <span className="truncate">
            {file ? file.name : t("external_document_choose_file")}
          </span>
        </label>
        <input
          id="external-document-file"
          type="file"
          accept="application/pdf,.pdf"
          className="sr-only"
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="external-document-number" className={labelCls}>
            {t("order_external_number")}
          </label>
          <input
            id="external-document-number"
            type="text"
            autoComplete="off"
            maxLength={EXTERNAL_NUMBER_MAX_LENGTH}
            className={inputCls}
            value={externalNumber}
            onChange={(e) => setExternalNumber(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="external-document-date" className={labelCls}>
            {t("external_contract_date")}
          </label>
          <input
            id="external-document-date"
            type="date"
            className={inputCls}
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
          />
          <p className={helpTextCls}>{t("external_contract_date_help")}</p>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondaryCls} onClick={close} disabled={saving}>
          {t("cancel", { defaultValue: "Cancel" })}
        </button>
        <button type="submit" className={btnPrimaryCls} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {hasDocument ? t("replace_external_document") : t("attach_external_document")}
        </button>
      </div>
    </form>
  );
}
